// ./src/components/generic/CardListSkeleton/CardListSkeleton.tsx
//
// A shared loading skeleton for card-row lists (schedule slots, questions, …).
// Matches the real row shell — bordered Card, optional leading checkbox, an optional leading icon, the card's text ROWS
// (`lines`, required — #117), and the row buttons (trailing, or in a row under the content) — so the skeleton↔content swap has no layout
// shift.
//
// ⚠ USE WITH `showSkeleton = !isLoaded` — the FIRST read, and nothing else. `loading &&
// items.length === 0` is TRUE on the very first render (before the request has started) AND on
// every refetch that happens to find the list empty, so an empty page read
// skeleton → "none" → skeleton → "none". Once the first read is back, a refetch keeps the rows OR
// the empty state on screen and `ContentLoadingOverlay` says it is reloading.
// ▶ npm run check-list-states
//
// ⚠⚠ THE CHECKBOX AND THE ACTION CIRCLES ARE TABLET-ONLY (`visibleFrom="sm"`) — owner, 2026-10-07: "loading skeletons
// must match separately large screen view and mobile one". On a phone a row's actions are SWIPE actions (no button on
// the card) and a selection checkbox exists only in selection mode, so drawing them there made every phone skeleton
// a different shape from the card that replaced it — at a dozen call sites at once, because the defaults drew them.
// ⚠⚠ AND `actions` IS REQUIRED (ROADMAP #110): it used to default to 2, so every caller that did not say otherwise drew
// two circles beside a card with three, four or five buttons — 12 of 21 callers were off. A default in a shared
// generic is the shape of every caller that does not override it. Count the buttons the TABLET card draws.
// ▶ npm run check-card-skeletons

import React from 'react';
import { Box, Card, Group, Stack, Skeleton } from '@mantine/core';

import { CardSkeletonActions, CardSkeletonCheckbox, CardSkeletonLines, type CardSkeletonRowSpec } from './CardSkeletonParts';

export interface CardListSkeletonProps {
  /** How many placeholder rows to render (default 4). */
  rows?: number;
  /** Leading checkbox square (bulk-selectable lists) — tablets only; a phone shows one only in selection mode. */
  withCheckbox?: boolean;
  /** How many row buttons the tablet card shows — tablets only; a phone swipes. 0 for a card with none. */
  actions: number;
  /**
   * ⚠ How many ROWS of text the real card shows (title, meta lines, a badge row). REQUIRED (ROADMAP #117): it drew two
   * for every caller, so a card of four or five grew the list the moment its data landed.
   */
  lines: number;
  /** Each row's text size when they are not all `md` (see `CardSkeletonLines`). */
  lineSizes?: CardSkeletonRowSpec[];
  /** The real card's gap between its rows, when it is not 4px. */
  lineGap?: number | 'xs';
  /**
   * Where the action circles show. Default `'tablet'` (a record list: a phone swipes). `'all'` only for a card whose
   * button is genuinely ON the phone card too — a picker's Add, never a record's edit/remove.
   */
  actionsOn?: 'tablet' | 'all';
  /** The card's buttons are TEXT buttons, not icons — their height and rough width (see `CardSkeletonActions`). */
  actionButton?: { height: 36 | 42; width: number };
  /** `'bottom'` for a card whose buttons sit in their own row under the content, aligned to the end. */
  actionsAt?: 'end' | 'bottom';
  /** Size in px of the card's leading icon / avatar, when it has one. */
  leading?: number;
  /** An avatar is a circle (default); a `ThemeIcon radius="md"` tile is a square. */
  leadingShape?: 'circle' | 'square';
  /** The real card's padding (default `md`, the house card). */
  padding?: 'sm' | 'md';
}

export const CardListSkeleton: React.FC<CardListSkeletonProps> = ({
  rows = 4,
  withCheckbox = false,
  actions,
  lines,
  lineSizes,
  lineGap,
  actionsOn = 'tablet',
  actionButton,
  actionsAt = 'end',
  leading,
  leadingShape = 'circle',
  padding = 'md',
}) => {
  const buttons = <CardSkeletonActions count={actions} on={actionsOn} button={actionButton} justify={actionsAt === 'bottom' ? 'flex-end' : undefined} />;

  return (
    <Stack gap="xs" aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <Card key={i} withBorder radius="md" padding={padding}>
          <Stack gap="sm">
            <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
              <Group gap="sm" wrap="nowrap" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
                <CardSkeletonCheckbox show={withCheckbox} />
                {leading ? (
                  <Box style={{ flexShrink: 0 }}>
                    <Skeleton circle={leadingShape === 'circle'} radius="md" height={leading} width={leading} />
                  </Box>
                ) : null}
                <CardSkeletonLines count={lines} sizes={lineSizes} gap={lineGap} />
              </Group>
              {actionsAt === 'end' && buttons}
            </Group>
            {actionsAt === 'bottom' && buttons}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
};
